/**
 * Centralised Quick Checkout New Relic events.
 *
 * All transient NR metrics for the Quick Checkout (AIP compact webview) flow live
 * here so they can be removed in one shot once the release is stable.
 */
import { BOOLEAN_AS_STRING } from "@swiggy-private/payment-types";

import { FEATURE_NR_CALL_STATUS } from "@checkout-core/constants/FeatureNRCallStatus";
import { NEWRELIC_TABLES } from "@checkout-core/constants/NewRelicTables";
import { getQuickCheckoutNRAttrs } from "@checkout-core/helpers/CartFlowNRAttrs";
import { recordNREvent } from "@checkout-core/newrelic";

const EVENT_TYPE = NEWRELIC_TABLES.QUICK_CHECKOUT_EVENTS;

export enum QUICK_CHECKOUT_NR_STATE {
    FLOW_ENTRY = "FLOW_ENTRY",
    CART_HYDRATE = "CART_HYDRATE",
    CART_HYDRATE_REJECT = "CART_HYDRATE_REJECT",
    CART_HYDRATE_DROP = "CART_HYDRATE_DROP",
    JS_BRIDGE = "JS_BRIDGE",
    /** Native-bridge outcomes (height change, exit flow). Nested `nbState` names the call. */
    NB_CALL = "NB_CALL",
    /** QC hop breadcrumbs (QC↔payments/MRO/turbo/exit). Nested `direction` names the hop. */
    NAV = "NAV",
    STATE_INVALIDATION = "STATE_INVALIDATION",
    QC_ERROR_PAGE = "QC_ERROR_PAGE",
    QC_ERROR_RETRY = "QC_ERROR_RETRY",
}

export enum QUICK_CHECKOUT_HYDRATE_TRANSPORT {
    PREFETCH = "prefetch",
    JS_BRIDGE = "js_bridge",
    EXISTING_CART = "existing_cart",
}

export enum QUICK_CHECKOUT_NB_STATE {
    WEBVIEW_LOADED = "WEBVIEW_LOADED",
    EXIT_FLOW = "EXIT_FLOW",
    REFRESH_PARENT = "REFRESH_PARENT",
    CHANGE_HEIGHT = "CHANGE_HEIGHT",
}

export enum QUICK_CHECKOUT_NAV_DIRECTION {
    QC_TO_PAYMENTS = "QC_TO_PAYMENTS",
    PAYMENTS_TO_QC = "PAYMENTS_TO_QC",
    QC_TO_MRO = "QC_TO_MRO",
    MRO_TO_QC = "MRO_TO_QC",
    QC_TO_TURBO_PO = "QC_TO_TURBO_PO",
    QC_EXIT = "QC_EXIT",
}

function withQCAttrs(extra?: Record<string, string>): Record<string, string> {
    const attrs = getQuickCheckoutNRAttrs();
    return {
        isQuickCheckoutFlow: attrs.isQuickCheckoutFlow,
        xCartType: attrs.xCartType,
        ...(extra || {}),
    };
}

function withHydrateTimestamps(params: {
    lastAppliedTimestamp?: number | null;
    currentTimestamp?: number | string | null;
}): Record<string, string> {
    const attrs: Record<string, string> = {};
    if (params.lastAppliedTimestamp != null) {
        attrs.lastAppliedTimestamp = String(params.lastAppliedTimestamp);
    }
    if (params.currentTimestamp != null && params.currentTimestamp !== "") {
        attrs.currentTimestamp = String(params.currentTimestamp);
    }
    return attrs;
}

/** Single spread point for QC eventType + flow attrs. */
function recordNREventForQC(payload: Record<string, string>, extra?: Record<string, string>): void {
    recordNREvent({
        eventType: EVENT_TYPE,
        ...payload,
        ...withQCAttrs(extra),
    });
}

export function recordQuickCheckoutFlowEntry(extra?: Record<string, string>): void {
    recordNREventForQC({ state: QUICK_CHECKOUT_NR_STATE.FLOW_ENTRY }, extra);
}

export function recordQuickCheckoutCartHydrate(params: {
    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT;
    status: FEATURE_NR_CALL_STATUS;
    cartUpdateStatus?: string;
    reason?: string;
    latencyMs?: number;
    lastAppliedTimestamp?: number | null;
    currentTimestamp?: number | string | null;
    extra?: Record<string, string>;
}): void {
    recordNREventForQC(
        {
            state: QUICK_CHECKOUT_NR_STATE.CART_HYDRATE,
            transport: params.transport,
            status: params.status,
            ...(params.cartUpdateStatus ? { cartUpdateStatus: params.cartUpdateStatus } : {}),
            ...(params.reason ? { reason: params.reason } : {}),
            ...(params.latencyMs != null ? { latencyMs: String(params.latencyMs) } : {}),
            ...withHydrateTimestamps(params),
        },
        params.extra,
    );
}

export function recordQuickCheckoutCartHydrateReject(params: {
    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT;
    reason: string;
    lastAppliedTimestamp?: number | null;
    currentTimestamp?: number | string | null;
    extra?: Record<string, string>;
}): void {
    recordNREventForQC(
        {
            state: QUICK_CHECKOUT_NR_STATE.CART_HYDRATE_REJECT,
            transport: params.transport,
            status: FEATURE_NR_CALL_STATUS.REJECTED,
            reason: params.reason,
            ...withHydrateTimestamps(params),
        },
        params.extra,
    );
}

export function recordQuickCheckoutCartHydrateDrop(params: {
    transport: QUICK_CHECKOUT_HYDRATE_TRANSPORT;
    reason: string;
    lastAppliedTimestamp?: number | null;
    currentTimestamp?: number | string | null;
    extra?: Record<string, string>;
}): void {
    recordNREventForQC(
        {
            state: QUICK_CHECKOUT_NR_STATE.CART_HYDRATE_DROP,
            transport: params.transport,
            status: FEATURE_NR_CALL_STATUS.SKIPPED,
            reason: params.reason,
            ...withHydrateTimestamps(params),
        },
        params.extra,
    );
}

export function recordQuickCheckoutJsBridge(params: {
    status: FEATURE_NR_CALL_STATUS;
    cartUpdateStatus?: string;
    reason?: string;
    ack: boolean;
    extra?: Record<string, string>;
}): void {
    recordNREventForQC(
        {
            state: QUICK_CHECKOUT_NR_STATE.JS_BRIDGE,
            status: params.status,
            ack: params.ack ? BOOLEAN_AS_STRING.TRUE : BOOLEAN_AS_STRING.FALSE,
            ...(params.cartUpdateStatus ? { cartUpdateStatus: params.cartUpdateStatus } : {}),
            ...(params.reason ? { reason: params.reason } : {}),
        },
        params.extra,
    );
}

export function recordQuickCheckoutNativeBridgeCall(params: {
    nbState: QUICK_CHECKOUT_NB_STATE | string;
    status: FEATURE_NR_CALL_STATUS;
    latencyMs?: number;
    direction?: string;
    height?: number | string;
    heightType?: string;
    extra?: Record<string, string>;
}): void {
    recordNREventForQC(
        {
            state: QUICK_CHECKOUT_NR_STATE.NB_CALL,
            nbState: params.nbState,
            status: params.status,
            ...(params.latencyMs != null ? { latencyMs: String(params.latencyMs) } : {}),
            ...(params.direction ? { direction: params.direction } : {}),
            ...(params.height != null ? { height: String(params.height) } : {}),
            ...(params.heightType ? { heightType: params.heightType } : {}),
        },
        params.extra,
    );
}

export function recordQuickCheckoutNav(params: {
    direction: QUICK_CHECKOUT_NAV_DIRECTION | string;
    extra?: Record<string, string>;
}): void {
    recordNREventForQC(
        {
            state: QUICK_CHECKOUT_NR_STATE.NAV,
            direction: params.direction,
        },
        params.extra,
    );
}

export function recordQuickCheckoutStateInvalidation(reason: string, extra?: Record<string, string>): void {
    recordNREventForQC({ state: QUICK_CHECKOUT_NR_STATE.STATE_INVALIDATION, reason }, extra);
}

export function recordQuickCheckoutErrorPage(extra?: Record<string, string>): void {
    recordNREventForQC({ state: QUICK_CHECKOUT_NR_STATE.QC_ERROR_PAGE }, extra);
}

export function recordQuickCheckoutErrorRetry(extra?: Record<string, string>): void {
    recordNREventForQC({ state: QUICK_CHECKOUT_NR_STATE.QC_ERROR_RETRY }, extra);
}
